import React, { useEffect, useState, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ANIMATION_DURATION = 25; // seconds for full animation
const STEPS = 720; // Two data points per day for smoother animation

const TokenomicsChart = () => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);
  
  // Calculate all data points once
  const totalDays = 365;
  const initialSupply = 420000000000000; // 420 trillion
  const dailyBurnRate = 0.05 / 30; // Convert monthly burn to daily (5% per month)
  const initialPrice = 0.0000001;
  const dailyPriceImpact = Math.pow(1.15, 1/30); // Convert monthly 1.15x to daily rate

  const allData = React.useMemo(() => {
    const labels = Array.from({ length: STEPS }, (_, i) => {
      const days = Math.floor((i / (STEPS - 1)) * totalDays);
      let label;
      
      if (days === 0) {
        label = 'Day 1';
      } else if (days === 30) {  
        label = 'Month 1';
      } else if (days % 30 === 0 && days > 0) {
        const months = Math.floor(days / 30);
        label = `Month ${months}`;
      } else {
        label = `Day ${days + 1}`;
      }
      
      return label;
    });

    const supplyData = [];
    const priceData = [];
    
    // Start both supply and price from 0
    supplyData.push(0);
    priceData.push(0);

    // Generate smooth data points
    for (let i = 1; i < STEPS; i++) {
      const progress = i / (STEPS - 1);
      const daysPassed = progress * totalDays;
      
      // Exponential growth for smoother transitions
      const supplyReduction = Math.pow(1 - dailyBurnRate, daysPassed);
      const priceIncrease = Math.pow(dailyPriceImpact, daysPassed);
      
      const currentSupply = initialSupply * supplyReduction;
      const currentPrice = initialPrice * priceIncrease;
      
      supplyData.push(currentSupply / 1000000000000); // Convert to trillions
      priceData.push(currentPrice);
    }

    return { labels, supplyData, priceData };
  }, []);

  // Custom easing function that starts slow and gradually speeds up
  const customEasing = (t) => {
    // Start very slow in the first 20%
    if (t < 0.2) {
      return t * 0.5; // Half speed at start
    }
    // Gradually speed up between 20% and 50%
    if (t < 0.5) {
      const normalizedT = (t - 0.2) / 0.3; // Normalize t to 0-1 for this range
      return 0.1 + (normalizedT * 0.4); // Smooth acceleration
    }
    // Normal speed for the rest
    return t;
  };

  const animate = (timestamp) => {
    if (!lastTimeRef.current) lastTimeRef.current = timestamp;
    const deltaTime = timestamp - lastTimeRef.current;
    lastTimeRef.current = timestamp;

    setProgress((prevProgress) => {
      const rawProgress = prevProgress + (deltaTime / (ANIMATION_DURATION * 1000));
      // Apply custom easing to the progress
      const easedProgress = customEasing(rawProgress % 1);
      return rawProgress >= 1 ? 0 : rawProgress;
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  // Calculate current data points based on progress
  const currentData = React.useMemo(() => {
    const currentIndex = Math.min(
      Math.floor(progress * STEPS),
      STEPS - 1
    );

    // Track shown labels to prevent duplicates
    const shownLabels = new Set();
    const filteredLabels = allData.labels.slice(0, currentIndex + 1).map((label, idx) => {
      if ((label === 'Day 1' || label.startsWith('Month'))) {
        if (!shownLabels.has(label)) {
          shownLabels.add(label);
          return label;
        }
      }
      return '';  // Return empty string for non-special labels or duplicates
    });
    
    return {
      labels: filteredLabels,
      datasets: [
        {
          label: 'Token Supply (Trillions)',
          data: allData.supplyData.slice(0, currentIndex + 1),
          borderColor: '#FF4800',
          backgroundColor: 'rgba(255, 72, 0, 0.1)',
          fill: true,
          yAxisID: 'y',
          tension: 0.6,
          borderWidth: 2,
        },
        {
          label: 'Token Price ($)',
          data: allData.priceData.slice(0, currentIndex + 1),
          borderColor: '#FFD700',
          backgroundColor: 'rgba(255, 215, 0, 0.1)',
          fill: true,
          yAxisID: 'y1',
          tension: 0.6,
          borderWidth: 2,
        }
      ]
    };
  }, [progress, allData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#FFFFFF',
          font: {
            size: 14
          }
        }
      },
      title: {
        display: true,
        text: 'Supply Reduction & Price Impact Simulation',
        color: '#FFFFFF',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.datasetIndex === 0) {
              label += Math.round(context.parsed.y).toLocaleString() + ' T';
            } else {
              label += '$' + context.parsed.y.toFixed(8);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Supply (Trillions)',
          color: '#FF4800',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawOnChartArea: true,
          drawTicks: false,
          drawBorder: false
        },
        ticks: {
          color: '#FFFFFF',
          callback: function(value) {
            return Math.round(value).toLocaleString();
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Price ($)',
          color: '#FFD700',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
          drawBorder: false
        },
        ticks: {
          color: '#FFFFFF',
          callback: function(value) {
            return '$' + value.toFixed(8);
          }
        }
      },
      x: {
        offset: true,
        grid: {
          display: false
        },
        ticks: {
          color: '#FFFFFF',
          font: {
            size: 12
          },
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          callback: function(value, index) {
            const label = this.getLabelForValue(value);
            return label || ''; // Return the pre-filtered label or empty string
          }
        }
      }
    }
  };

  // Start/stop animation based on visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          setProgress(0); // Reset to start when becoming visible
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px' // Start animation slightly before the chart is in view
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle animation start/stop with debouncing
  useEffect(() => {
    let debounceTimeout;

    if (isVisible) {
      // Add small delay before starting animation
      debounceTimeout = setTimeout(() => {
        lastTimeRef.current = null;
        animationRef.current = requestAnimationFrame(animate);
      }, 100);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible]);

  return (
    <div ref={containerRef} className="w-full h-[400px] p-4 backdrop-blur-sm border border-[#FF4800]/20 rounded-lg">
      <Line options={options} data={currentData} />
    </div>
  );
};

export default TokenomicsChart;
